import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';
import { ShieldCheck, User, Phone, Mail, Lock, ArrowRight, X, CheckCircle2 } from 'lucide-react';

const InputRow = ({ icon: Icon, ...props }) => (
  <div className="relative">
    <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-2)]" />
    <input
      {...props}
      className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-lg pl-9 pr-3 py-3 text-[14px] text-white placeholder:text-[var(--muted-2)] focus:outline-none focus:border-[var(--orange)] transition-colors"
    />
  </div>
);

const Label = ({ children }) => (
  <label className="text-[10px] font-mono tracking-widest text-[var(--muted-2)] block mb-1.5">{children}</label>
);

export default function AuthModal() {
  const {
    authModalOpen, authView, setAuthView, closeAuth,
    register, login, forgotPassword, resetPassword, resetToken,
  } = useApp();

  const [signup, setSignup] = useState({ fullName: '', phone: '', email: '', password: '', role: 'Buyer / Investor', entityType: 'Individual Investor' });
  const [signin, setSignin] = useState({ email: '', password: '' });
  const [forgotEmail, setForgotEmail] = useState('');
  const [reset, setReset] = useState({ password: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => { setForgotSent(false); }, [authView, authModalOpen]);

  const doRegister = async () => {
    if (!signup.fullName || !signup.email || !signup.password) {
      toast.error('Please fill name, email and password');
      return;
    }
    setBusy(true);
    const res = await register(signup);
    setBusy(false);
    if (res.ok) {
      toast.success('Account created', { description: 'A confirmation email has been sent to your inbox.' });
      closeAuth();
    } else {
      toast.error(res.error);
    }
  };

  const doLogin = async () => {
    if (!signin.email || !signin.password) { toast.error('Enter email and password'); return; }
    setBusy(true);
    const res = await login(signin.email, signin.password);
    setBusy(false);
    if (res.ok) { toast.success('Signed in to Live ATS'); closeAuth(); }
    else toast.error(res.error);
  };

  const doForgot = async () => {
    if (!forgotEmail) { toast.error('Enter your email'); return; }
    setBusy(true);
    const res = await forgotPassword(forgotEmail);
    setBusy(false);
    if (res.ok) { setForgotSent(true); toast.success('Reset link sent', { description: 'Check your email for the password reset link.' }); }
    else toast.error(res.error);
  };

  const doReset = async () => {
    if (reset.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (reset.password !== reset.confirm) { toast.error('Passwords do not match'); return; }
    setBusy(true);
    const res = await resetPassword(resetToken, reset.password);
    setBusy(false);
    if (res.ok) { toast.success('Password reset', { description: 'You can now sign in with your new password.' }); setAuthView('signin'); }
    else toast.error(res.error);
  };

  const roles = ['Buyer / Investor', 'Seller / Shareholder'];
  const entities = ['Individual Investor', 'Institutional / Fund', 'Family Office', 'Corporate Treasury'];

  const title = authView === 'reset' ? 'SET A NEW PASSWORD'
    : authView === 'forgot' ? 'RESET YOUR PASSWORD'
    : authView === 'signin' ? 'SIGN IN TO LIVE ATS'
    : 'CREATE REAL INVESTOR ACCOUNT';

  return (
    <Dialog open={authModalOpen} onOpenChange={(o) => !o && closeAuth()}>
      <DialogContent className="bg-[var(--panel)] border-[var(--border)] text-[var(--text)] max-w-md p-0 gap-0 [&>button]:hidden max-h-[92vh] overflow-y-auto">
        <button onClick={closeAuth} className="absolute right-4 top-4 text-[var(--muted)] hover:text-white z-10"><X size={18} /></button>
        <div className="px-7 pt-8 pb-6">
          <div className="flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[var(--orange)] to-[#c2410c] flex items-center justify-center">
              <ShieldCheck size={22} className="text-white" />
            </div>
            <h2 className="font-display font-bold text-[22px] text-white mt-4 tracking-tight">{title}</h2>
            <p className="text-[10px] font-mono tracking-[0.14em] text-[var(--muted-2)] mt-2">FINRA / SEC FORM BD · ATS COMPLIANT SECONDARY MARKETS</p>
          </div>

          {(authView === 'signup' || authView === 'signin') && (
            <div className="grid grid-cols-2 gap-2 mt-6 p-1 rounded-lg bg-[var(--bg)] border border-[var(--border)]">
              <button onClick={() => setAuthView('signup')} className={`py-2.5 rounded-md text-[12px] font-semibold tracking-wide transition-colors ${authView === 'signup' ? 'bg-[var(--orange)] text-white' : 'text-[var(--muted)] hover:text-white'}`}>REAL SIGN UP</button>
              <button onClick={() => setAuthView('signin')} className={`py-2.5 rounded-md text-[12px] font-semibold tracking-wide transition-colors ${authView === 'signin' ? 'bg-[var(--orange)] text-white' : 'text-[var(--muted)] hover:text-white'}`}>LIVE SIGN IN</button>
            </div>
          )}

          {/* SIGN UP */}
          {authView === 'signup' && (
            <div className="mt-6 space-y-4">
              <div><Label>FULL LEGAL NAME (AS PER PASSPORT / ID)</Label>
                <InputRow icon={User} placeholder="e.g. Om Sant" value={signup.fullName} onChange={(e) => setSignup({ ...signup, fullName: e.target.value })} /></div>
              <div><Label>PHONE NUMBER (FOR 2FA SMS)</Label>
                <InputRow icon={Phone} placeholder="+1 (555) 000-0000" value={signup.phone} onChange={(e) => setSignup({ ...signup, phone: e.target.value })} /></div>
              <div><Label>EMAIL ADDRESS</Label>
                <InputRow icon={Mail} type="email" placeholder="name@example.com" value={signup.email} onChange={(e) => setSignup({ ...signup, email: e.target.value })} /></div>
              <div><Label>PASSWORD</Label>
                <InputRow icon={Lock} type="password" placeholder="••••••••" value={signup.password} onChange={(e) => setSignup({ ...signup, password: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>ACCOUNT ROLE</Label>
                  <select value={signup.role} onChange={(e) => setSignup({ ...signup, role: e.target.value })} className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-3 text-[13px] text-white focus:outline-none focus:border-[var(--orange)]">
                    {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select></div>
                <div><Label>ENTITY TYPE</Label>
                  <select value={signup.entityType} onChange={(e) => setSignup({ ...signup, entityType: e.target.value })} className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-3 text-[13px] text-white focus:outline-none focus:border-[var(--orange)]">
                    {entities.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select></div>
              </div>
              <button onClick={doRegister} disabled={busy} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-lg bg-[var(--orange)] text-white font-semibold text-[14px] hover:bg-[var(--orange-bright)] transition-colors disabled:opacity-60">
                <ArrowRight size={16} /> {busy ? 'REGISTERING…' : 'REGISTER REAL ACCOUNT'}
              </button>
              <button onClick={() => setAuthView('signin')} className="w-full text-center text-[11px] font-mono tracking-wider text-[var(--muted)] hover:text-white transition-colors">ALREADY HAVE AN ACCOUNT? SIGN IN</button>
            </div>
          )}

          {/* SIGN IN */}
          {authView === 'signin' && (
            <div className="mt-6 space-y-4">
              <div><Label>EMAIL ADDRESS</Label>
                <InputRow icon={Mail} type="email" placeholder="name@example.com" value={signin.email} onChange={(e) => setSignin({ ...signin, email: e.target.value })} /></div>
              <div><Label>PASSWORD</Label>
                <InputRow icon={Lock} type="password" placeholder="••••••••" value={signin.password} onChange={(e) => setSignin({ ...signin, password: e.target.value })} /></div>
              <button onClick={doLogin} disabled={busy} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-lg bg-[var(--orange)] text-white font-semibold text-[14px] hover:bg-[var(--orange-bright)] transition-colors disabled:opacity-60">
                <ArrowRight size={16} /> {busy ? 'SIGNING IN…' : 'SIGN IN TO ACCOUNT'}
              </button>
              <button onClick={() => setAuthView('signup')} className="w-full text-center text-[11px] font-mono tracking-wider text-[var(--muted)] hover:text-white transition-colors">DON'T HAVE AN ACCOUNT? REGISTER NEW ACCOUNT</button>
              <button onClick={() => setAuthView('forgot')} className="w-full text-center text-[11px] font-mono tracking-wider text-[var(--orange)] hover:text-[var(--orange-bright)] transition-colors">FORGOT PASSWORD?</button>
            </div>
          )}

          {/* FORGOT */}
          {authView === 'forgot' && (
            forgotSent ? (
              <div className="mt-6 text-center">
                <CheckCircle2 size={40} className="mx-auto text-[var(--green)]" />
                <p className="text-[14px] text-white mt-4 font-medium">Reset link sent</p>
                <p className="text-[12.5px] text-[var(--muted)] mt-2">If an account exists for <span className="text-white">{forgotEmail}</span>, a password reset link is on its way. The link expires in 30 minutes.</p>
                <button onClick={() => setAuthView('signin')} className="mt-6 w-full py-3 rounded-lg bg-[var(--panel-2)] border border-[var(--border)] text-white text-[13px] hover:border-[#3a3a42] transition-colors">Back to Sign In</button>
              </div>
            ) : (
              <div className="mt-6 space-y-4">
                <p className="text-[12.5px] text-[var(--muted)]">Enter the email on your account and we'll send you a secure link to reset your password.</p>
                <div><Label>EMAIL ADDRESS</Label>
                  <InputRow icon={Mail} type="email" placeholder="name@example.com" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} /></div>
                <button onClick={doForgot} disabled={busy} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-lg bg-[var(--orange)] text-white font-semibold text-[14px] hover:bg-[var(--orange-bright)] transition-colors disabled:opacity-60">
                  <Mail size={16} /> {busy ? 'SENDING…' : 'SEND RESET LINK'}
                </button>
                <button onClick={() => setAuthView('signin')} className="w-full text-center text-[11px] font-mono tracking-wider text-[var(--muted)] hover:text-white transition-colors">← BACK TO SIGN IN</button>
              </div>
            )
          )}

          {/* RESET */}
          {authView === 'reset' && (
            <div className="mt-6 space-y-4">
              <p className="text-[12.5px] text-[var(--muted)]">Choose a new password for your ApexForge account.</p>
              <div><Label>NEW PASSWORD</Label>
                <InputRow icon={Lock} type="password" placeholder="••••••••" value={reset.password} onChange={(e) => setReset({ ...reset, password: e.target.value })} /></div>
              <div><Label>CONFIRM NEW PASSWORD</Label>
                <InputRow icon={Lock} type="password" placeholder="••••••••" value={reset.confirm} onChange={(e) => setReset({ ...reset, confirm: e.target.value })} /></div>
              <button onClick={doReset} disabled={busy} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-lg bg-[var(--orange)] text-white font-semibold text-[14px] hover:bg-[var(--orange-bright)] transition-colors disabled:opacity-60">
                <ShieldCheck size={16} /> {busy ? 'RESETTING…' : 'RESET PASSWORD'}
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
